/**
 * Settings panel — LLMs, API Keys, Debug, and Config tabs.
 * Registered with panelManager as position: right, id: settings.
 *
 * Non-secret preferences (model selections, endpoints) → localStorage.
 * Secrets (API keys) → OS keychain on desktop, localStorage on web.
 */

import { eventBus } from '@/core/events';
import { getProvider, discoverAvailable, clearAvailabilityCache } from '@/intelligence/ai/provider-registry';
import { SOURCE_REGISTRY } from '@/config/sources';
import {
  getRequestLog,
  clearRequestLog,
  onLogEntry,
  type RequestLogEntry,
} from '@/intelligence/ai/request-logger';
import { secretStore, SECRET_KEYS } from '@/storage/keychain';
import { getActiveBindings, formatShortcut } from '@/utils/keyboard-shortcuts';
import { isTauriRuntime } from '@/utils/tauri';
import {
  loadNotificationSettings,
  saveNotificationSettings,
  requestNotificationPermission,
  isNotificationSupported,
  type NotificationSettings,
} from '@/ui/components/native-notifications';

// --- Panel cleanup (prevents listener leaks on rebuild) ---

let panelCleanupFns: (() => void)[] = [];

// --- Settings persistence (non-secret preferences only) ---

const STORAGE_KEY = 'sentinel-ai-settings';
const VERBOSE_KEY = 'sentinel-verbose';
const DISABLED_SOURCES_KEY = 'sentinel-disabled-sources';

const IS_DESKTOP = isTauriRuntime();

interface AIPreferences {
  ollama: { endpoint: string; model: string };
  anthropic: { model: string };
  openrouter: { model: string };
}

function loadPreferences(): AIPreferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, Record<string, string>>;
      return {
        ollama: {
          endpoint: parsed.ollama?.endpoint ?? 'http://localhost:11434',
          model: parsed.ollama?.model ?? '',
        },
        anthropic: { model: parsed.anthropic?.model ?? '' },
        openrouter: { model: parsed.openrouter?.model ?? '' },
      };
    }
  } catch { /* ignore corrupt data */ }
  return {
    ollama: { endpoint: 'http://localhost:11434', model: '' },
    anthropic: { model: '' },
    openrouter: { model: '' },
  };
}

function savePreferences(prefs: AIPreferences): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
}

// --- Debounce helper ---

function debounce(fn: () => void, ms: number): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(fn, ms);
  };
}

// --- Status dot helper ---

function statusDot(state: 'green' | 'gray' | 'red'): HTMLElement {
  const dot = document.createElement('span');
  const colorMap = { green: 'var(--accent-green)', gray: 'var(--text-muted)', red: 'var(--accent-red)' };
  dot.style.cssText = `
    display: inline-block; width: 8px; height: 8px; border-radius: 50%;
    background: ${colorMap[state]}; margin-right: 6px; flex-shrink: 0;
  `;
  return dot;
}

// --- Section heading ---

function sectionLabel(text: string): HTMLElement {
  const el = document.createElement('div');
  el.style.cssText = `
    font-size: 10px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.05em; color: var(--text-muted); margin: 12px 0 6px;
  `;
  el.textContent = text;
  return el;
}

// --- Provider row builder ---

interface ProviderRowOptions {
  providerId: string;
  label: string;
  fields: HTMLElement[];
  verifyBtn: HTMLButtonElement;
  statusEl: HTMLElement;
}

function buildProviderRow(opts: ProviderRowOptions): HTMLElement {
  const row = document.createElement('div');
  row.style.cssText = 'margin-bottom: 14px; padding: 10px; background: var(--bg-secondary); border-radius: 6px;';

  // Header line: status dot + name
  const header = document.createElement('div');
  header.style.cssText = 'display: flex; align-items: center; margin-bottom: 8px;';
  header.appendChild(opts.statusEl);
  const name = document.createElement('span');
  name.style.cssText = 'font-size: 12px; font-weight: 600; color: var(--text-primary);';
  name.textContent = opts.label;
  header.appendChild(name);
  row.appendChild(header);

  for (const field of opts.fields) {
    row.appendChild(field);
  }

  row.appendChild(opts.verifyBtn);
  return row;
}

// --- Input helpers ---

function textInput(placeholder: string, value: string, type = 'text'): HTMLInputElement {
  const input = document.createElement('input');
  input.type = type;
  input.placeholder = placeholder;
  input.value = value;
  input.setAttribute('aria-label', placeholder);
  input.style.cssText = `
    width: 100%; box-sizing: border-box; padding: 6px 8px; margin-bottom: 6px;
    font-size: 12px; background: var(--bg-primary); color: var(--text-primary);
    border: 1px solid var(--border-primary); border-radius: 4px; outline: none;
  `;
  return input;
}

function passwordInput(placeholder: string, value: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.style.cssText = 'position: relative; margin-bottom: 6px;';

  const input = document.createElement('input');
  input.type = 'password';
  input.placeholder = placeholder;
  input.value = value;
  input.setAttribute('aria-label', placeholder);
  input.style.cssText = `
    width: 100%; box-sizing: border-box; padding: 6px 28px 6px 8px;
    font-size: 12px; background: var(--bg-primary); color: var(--text-primary);
    border: 1px solid var(--border-primary); border-radius: 4px; outline: none;
  `;

  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.style.cssText = `
    position: absolute; right: 4px; top: 50%; transform: translateY(-50%);
    background: none; border: none; cursor: pointer; font-size: 12px;
    color: var(--text-muted); padding: 2px 4px;
  `;
  toggle.textContent = '\u{1F441}'; // eye
  toggle.title = 'Show/hide';
  toggle.setAttribute('aria-label', 'Toggle password visibility');
  toggle.onclick = () => {
    input.type = input.type === 'password' ? 'text' : 'password';
  };

  wrapper.appendChild(input);
  wrapper.appendChild(toggle);
  return wrapper;
}

function selectDropdown(options: string[], selected: string, ariaLabel?: string): HTMLSelectElement {
  const sel = document.createElement('select');
  if (ariaLabel) sel.setAttribute('aria-label', ariaLabel);
  sel.style.cssText = `
    width: 100%; box-sizing: border-box; padding: 6px 8px; margin-bottom: 6px;
    font-size: 12px; background: var(--bg-primary); color: var(--text-primary);
    border: 1px solid var(--border-primary); border-radius: 4px; outline: none;
  `;

  if (options.length === 0) {
    const opt = document.createElement('option');
    opt.textContent = 'No models found';
    opt.disabled = true;
    sel.appendChild(opt);
  }

  for (const model of options) {
    const opt = document.createElement('option');
    opt.value = model;
    opt.textContent = model;
    if (model === selected) opt.selected = true;
    sel.appendChild(opt);
  }

  return sel;
}

function verifyButton(): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = 'Verify';
  btn.style.cssText = `
    font-size: 11px; padding: 4px 12px; border-radius: 4px; cursor: pointer;
    background: var(--accent); color: var(--bg-primary); border: none;
    margin-top: 4px;
  `;
  return btn;
}

function inlineResult(success: boolean, message: string): HTMLElement {
  const el = document.createElement('span');
  el.style.cssText = `
    font-size: 11px; margin-left: 8px;
    color: ${success ? 'var(--accent-green)' : 'var(--accent-red)'};
  `;
  el.textContent = success ? `\u2713 ${message}` : `\u2717 ${message}`;
  return el;
}

// --- Keychain storage label (desktop only) ---

function keychainBadge(): HTMLElement {
  const badge = document.createElement('span');
  badge.style.cssText = `
    display: inline-flex; align-items: center; gap: 3px;
    font-size: 10px; color: var(--text-muted); margin-left: auto;
  `;
  badge.textContent = '\u{1F512} Stored in OS keychain';
  return badge;
}

// --- Manual text input fallback ---

function modelInputFallback(value: string): HTMLInputElement {
  return textInput('Enter model name manually', value);
}

// --- Refresh providers helper ---

function refreshProviders(): void {
  clearAvailabilityCache();
  discoverAvailable().catch((err) => console.warn('[Sentinel] Provider refresh failed:', err));
}

// ========================================================
// Tab 1: LLMs
// ========================================================

function buildLLMsTab(
  prefs: AIPreferences,
  onPreferencesChange: () => void,
  signal: AbortSignal,
  isDisposed: () => boolean,
): HTMLElement {
  const tab = document.createElement('div');
  tab.style.cssText = 'padding: 8px 0;';

  // Track status dots for live updates
  const statusDots = new Map<string, HTMLElement>();

  // --- Ollama ---
  const ollamaDot = statusDot('gray');
  statusDots.set('ollama', ollamaDot);

  const ollamaEndpoint = textInput('Endpoint URL', prefs.ollama.endpoint, 'url');
  const ollamaModelSelect = selectDropdown([], prefs.ollama.model, 'Ollama model');
  const ollamaManualInput = modelInputFallback(prefs.ollama.model);
  ollamaManualInput.style.display = 'none';

  const ollamaRefresh = document.createElement('button');
  ollamaRefresh.type = 'button';
  ollamaRefresh.textContent = 'Refresh';
  ollamaRefresh.style.cssText = `
    font-size: 10px; padding: 2px 8px; border-radius: 3px; cursor: pointer;
    background: transparent; color: var(--text-secondary); border: 1px solid var(--border-primary);
    margin-bottom: 6px; margin-left: 4px;
  `;

  const modelRow = document.createElement('div');
  modelRow.style.cssText = 'display: flex; align-items: flex-start; gap: 4px;';
  const modelSelectWrapper = document.createElement('div');
  modelSelectWrapper.style.cssText = 'flex: 1;';
  modelSelectWrapper.appendChild(ollamaModelSelect);
  modelSelectWrapper.appendChild(ollamaManualInput);
  modelRow.appendChild(modelSelectWrapper);
  modelRow.appendChild(ollamaRefresh);

  async function refreshOllamaModels(): Promise<void> {
    const provider = getProvider('ollama');
    if (!provider || isDisposed()) return;
    try {
      const models = await provider.listModels();
      if (isDisposed()) return;
      if (models.length === 0) {
        ollamaModelSelect.style.display = 'none';
        ollamaManualInput.style.display = '';
      } else {
        ollamaModelSelect.style.display = '';
        ollamaManualInput.style.display = 'none';
        ollamaModelSelect.textContent = '';
        for (const m of models) {
          const opt = document.createElement('option');
          opt.value = m;
          opt.textContent = m;
          if (m === prefs.ollama.model) opt.selected = true;
          ollamaModelSelect.appendChild(opt);
        }
      }
    } catch {
      ollamaModelSelect.style.display = 'none';
      ollamaManualInput.style.display = '';
    }
  }

  ollamaRefresh.addEventListener('click', () => { refreshOllamaModels(); }, { signal });

  const debouncedEndpointSave = debounce(() => {
    if (isDisposed()) return;
    prefs.ollama.endpoint = ollamaEndpoint.value;
    onPreferencesChange();
  }, 500);
  ollamaEndpoint.addEventListener('input', debouncedEndpointSave, { signal });

  ollamaModelSelect.addEventListener('change', () => {
    prefs.ollama.model = ollamaModelSelect.value;
    onPreferencesChange();
  }, { signal });
  ollamaManualInput.addEventListener('input', debounce(() => {
    if (isDisposed()) return;
    prefs.ollama.model = ollamaManualInput.value;
    onPreferencesChange();
  }, 500), { signal });

  const ollamaVerify = verifyButton();
  ollamaVerify.addEventListener('click', async () => {
    ollamaVerify.disabled = true;
    ollamaVerify.textContent = 'Testing...';
    // Remove previous result
    const prev = ollamaVerify.parentElement?.querySelector('.verify-result');
    if (prev) prev.remove();

    const provider = getProvider('ollama');
    if (!provider) {
      ollamaVerify.disabled = false;
      ollamaVerify.textContent = 'Verify';
      return;
    }

    try {
      const result = await provider.validate();
      if (isDisposed()) return;
      ollamaVerify.disabled = false;
      ollamaVerify.textContent = 'Verify';
      const resultEl = inlineResult(result.valid, result.valid ? 'Connected' : (result.error ?? 'Failed'));
      resultEl.className = 'verify-result';
      ollamaVerify.parentElement?.appendChild(resultEl);
      ollamaDot.style.background = result.valid ? 'var(--accent-green)' : 'var(--accent-red)';
    } catch (err) {
      if (isDisposed()) return;
      ollamaVerify.disabled = false;
      ollamaVerify.textContent = 'Verify';
      const resultEl = inlineResult(false, err instanceof Error ? err.message : 'Verification failed');
      resultEl.className = 'verify-result';
      ollamaVerify.parentElement?.appendChild(resultEl);
    }
  }, { signal });

  tab.appendChild(buildProviderRow({
    providerId: 'ollama',
    label: 'Ollama (Local)',
    fields: [ollamaEndpoint, modelRow],
    verifyBtn: ollamaVerify,
    statusEl: ollamaDot,
  }));

  // --- Anthropic Claude ---
  const anthropicDot = statusDot('gray');
  statusDots.set('anthropic', anthropicDot);

  const anthropicKeyWrapper = passwordInput('Anthropic API Key', '');
  const anthropicKeyInput = anthropicKeyWrapper.querySelector('input')!;

  // Async-load API key from keychain
  secretStore.get(SECRET_KEYS.ANTHROPIC_API_KEY).then(val => {
    if (isDisposed()) return;
    if (val) anthropicKeyInput.value = val;
  }).catch((err) => console.warn('[Sentinel] Keychain read failed:', err));

  // Desktop: editable, save to keychain. Web: read-only.
  if (!IS_DESKTOP) {
    anthropicKeyInput.readOnly = true;
    anthropicKeyInput.style.opacity = '0.6';
    anthropicKeyInput.style.cursor = 'not-allowed';
    anthropicKeyInput.placeholder = 'Set via environment variables';
  }

  // Keychain badge on desktop
  if (IS_DESKTOP) {
    const badge = keychainBadge();
    badge.style.marginBottom = '6px';
    anthropicKeyWrapper.appendChild(badge);
  }

  // Workspace id — required by identity-linked API keys, meaningless for
  // classic keys. Plain text (it is an id, not a secret) but stored via the
  // same keychain path as the key. The sidecar reads it at spawn, so a
  // change takes effect on app restart, same as the key itself.
  const anthropicWorkspaceInput = textInput('Workspace ID (identity-linked keys only)', '');

  secretStore.get(SECRET_KEYS.ANTHROPIC_WORKSPACE_ID).then(val => {
    if (isDisposed()) return;
    if (val) anthropicWorkspaceInput.value = val;
  }).catch((err) => console.warn('[Sentinel] Keychain read failed:', err));

  if (!IS_DESKTOP) {
    anthropicWorkspaceInput.readOnly = true;
    anthropicWorkspaceInput.style.opacity = '0.6';
    anthropicWorkspaceInput.style.cursor = 'not-allowed';
    anthropicWorkspaceInput.placeholder = 'Set via environment variables';
  }

  const anthropicModels = ['claude-sonnet-5', 'claude-haiku-4-5-20251001', 'claude-opus-5'];
  const anthropicModelSelect = selectDropdown(anthropicModels, prefs.anthropic.model || anthropicModels[1], 'Anthropic model');

  const debouncedAnthropicKeySave = debounce(() => {
    if (isDisposed()) return;
    if (IS_DESKTOP) {
      secretStore.set(SECRET_KEYS.ANTHROPIC_API_KEY, anthropicKeyInput.value).catch((err) => console.warn('[Sentinel] Keychain write failed:', err));
    }
    refreshProviders();
  }, 500);
  anthropicKeyInput.addEventListener('input', debouncedAnthropicKeySave, { signal });

  const debouncedAnthropicWorkspaceSave = debounce(() => {
    if (isDisposed()) return;
    if (IS_DESKTOP) {
      secretStore.set(SECRET_KEYS.ANTHROPIC_WORKSPACE_ID, anthropicWorkspaceInput.value.trim()).catch((err) => console.warn('[Sentinel] Keychain write failed:', err));
    }
  }, 500);
  anthropicWorkspaceInput.addEventListener('input', debouncedAnthropicWorkspaceSave, { signal });

  anthropicModelSelect.addEventListener('change', () => {
    prefs.anthropic.model = anthropicModelSelect.value;
    onPreferencesChange();
  }, { signal });

  const anthropicVerify = verifyButton();
  anthropicVerify.addEventListener('click', async () => {
    anthropicVerify.disabled = true;
    anthropicVerify.textContent = 'Testing...';
    const prev = anthropicVerify.parentElement?.querySelector('.verify-result');
    if (prev) prev.remove();

    const provider = getProvider('anthropic');
    if (!provider) {
      anthropicVerify.disabled = false;
      anthropicVerify.textContent = 'Verify';
      return;
    }

    try {
      const result = await provider.validate();
      if (isDisposed()) return;
      anthropicVerify.disabled = false;
      anthropicVerify.textContent = 'Verify';
      const resultEl = inlineResult(result.valid, result.valid ? 'Connected' : (result.error ?? 'Failed'));
      resultEl.className = 'verify-result';
      anthropicVerify.parentElement?.appendChild(resultEl);
      anthropicDot.style.background = result.valid ? 'var(--accent-green)' : 'var(--accent-red)';
    } catch (err) {
      if (isDisposed()) return;
      anthropicVerify.disabled = false;
      anthropicVerify.textContent = 'Verify';
      const resultEl = inlineResult(false, err instanceof Error ? err.message : 'Verification failed');
      resultEl.className = 'verify-result';
      anthropicVerify.parentElement?.appendChild(resultEl);
    }
  }, { signal });

  tab.appendChild(buildProviderRow({
    providerId: 'anthropic',
    label: 'Anthropic Claude',
    fields: [anthropicKeyWrapper, anthropicWorkspaceInput, anthropicModelSelect],
    verifyBtn: anthropicVerify,
    statusEl: anthropicDot,
  }));

  // --- OpenRouter ---
  const openrouterDot = statusDot('gray');
  statusDots.set('openrouter', openrouterDot);

  const openrouterKeyWrapper = passwordInput('OpenRouter API Key', '');
  const openrouterKeyInput = openrouterKeyWrapper.querySelector('input')!;

  // Async-load API key from keychain
  secretStore.get(SECRET_KEYS.OPENROUTER_API_KEY).then(val => {
    if (isDisposed()) return;
    if (val) openrouterKeyInput.value = val;
  }).catch((err) => console.warn('[Sentinel] Keychain read failed:', err));

  // Desktop: editable, save to keychain. Web: read-only.
  if (!IS_DESKTOP) {
    openrouterKeyInput.readOnly = true;
    openrouterKeyInput.style.opacity = '0.6';
    openrouterKeyInput.style.cursor = 'not-allowed';
    openrouterKeyInput.placeholder = 'Set via environment variables';
  }

  if (IS_DESKTOP) {
    const badge = keychainBadge();
    badge.style.marginBottom = '6px';
    openrouterKeyWrapper.appendChild(badge);
  }

  const openrouterModelSelect = selectDropdown([], prefs.openrouter.model, 'OpenRouter model');
  const openrouterManualInput = modelInputFallback(prefs.openrouter.model);
  openrouterManualInput.style.display = 'none';

  async function refreshOpenRouterModels(): Promise<void> {
    const provider = getProvider('openrouter');
    if (!provider || isDisposed()) return;
    try {
      const models = await provider.listModels();
      if (isDisposed()) return;
      if (models.length === 0) {
        openrouterModelSelect.style.display = 'none';
        openrouterManualInput.style.display = '';
      } else {
        openrouterModelSelect.style.display = '';
        openrouterManualInput.style.display = 'none';
        openrouterModelSelect.textContent = '';
        for (const m of models) {
          const opt = document.createElement('option');
          opt.value = m;
          opt.textContent = m;
          if (m === prefs.openrouter.model) opt.selected = true;
          openrouterModelSelect.appendChild(opt);
        }
      }
    } catch {
      openrouterModelSelect.style.display = 'none';
      openrouterManualInput.style.display = '';
    }
  }

  const debouncedOpenRouterKeySave = debounce(() => {
    if (isDisposed()) return;
    if (IS_DESKTOP) {
      secretStore.set(SECRET_KEYS.OPENROUTER_API_KEY, openrouterKeyInput.value).catch((err) => console.warn('[Sentinel] Keychain write failed:', err));
    }
    refreshProviders();
  }, 500);
  openrouterKeyInput.addEventListener('input', debouncedOpenRouterKeySave, { signal });

  openrouterModelSelect.addEventListener('change', () => {
    prefs.openrouter.model = openrouterModelSelect.value;
    onPreferencesChange();
  }, { signal });
  openrouterManualInput.addEventListener('input', debounce(() => {
    if (isDisposed()) return;
    prefs.openrouter.model = openrouterManualInput.value;
    onPreferencesChange();
  }, 500), { signal });

  const openrouterVerify = verifyButton();
  openrouterVerify.addEventListener('click', async () => {
    openrouterVerify.disabled = true;
    openrouterVerify.textContent = 'Testing...';
    const prev = openrouterVerify.parentElement?.querySelector('.verify-result');
    if (prev) prev.remove();

    const provider = getProvider('openrouter');
    if (!provider) {
      openrouterVerify.disabled = false;
      openrouterVerify.textContent = 'Verify';
      return;
    }

    try {
      const result = await provider.validate();
      if (isDisposed()) return;
      openrouterVerify.disabled = false;
      openrouterVerify.textContent = 'Verify';
      const resultEl = inlineResult(result.valid, result.valid ? 'Connected' : (result.error ?? 'Failed'));
      resultEl.className = 'verify-result';
      openrouterVerify.parentElement?.appendChild(resultEl);
      openrouterDot.style.background = result.valid ? 'var(--accent-green)' : 'var(--accent-red)';
    } catch (err) {
      if (isDisposed()) return;
      openrouterVerify.disabled = false;
      openrouterVerify.textContent = 'Verify';
      const resultEl = inlineResult(false, err instanceof Error ? err.message : 'Verification failed');
      resultEl.className = 'verify-result';
      openrouterVerify.parentElement?.appendChild(resultEl);
    }
  }, { signal });

  const openrouterModelRow = document.createElement('div');
  openrouterModelRow.style.cssText = 'display: flex; align-items: flex-start; gap: 4px;';
  const openrouterModelWrapper = document.createElement('div');
  openrouterModelWrapper.style.cssText = 'flex: 1;';
  openrouterModelWrapper.appendChild(openrouterModelSelect);
  openrouterModelWrapper.appendChild(openrouterManualInput);
  openrouterModelRow.appendChild(openrouterModelWrapper);

  tab.appendChild(buildProviderRow({
    providerId: 'openrouter',
    label: 'OpenRouter',
    fields: [openrouterKeyWrapper, openrouterModelRow],
    verifyBtn: openrouterVerify,
    statusEl: openrouterDot,
  }));

  // --- Browser ML (coming soon) ---
  const browserRow = document.createElement('div');
  browserRow.style.cssText = `
    margin-bottom: 8px; padding: 10px; background: var(--bg-secondary);
    border-radius: 6px; opacity: 0.5;
  `;
  const browserHeader = document.createElement('div');
  browserHeader.style.cssText = 'display: flex; align-items: center;';
  browserHeader.appendChild(statusDot('gray'));
  const browserLabel = document.createElement('span');
  browserLabel.style.cssText = 'font-size: 12px; color: var(--text-muted);';
  browserLabel.textContent = 'Browser ML — coming soon';
  browserHeader.appendChild(browserLabel);
  browserRow.appendChild(browserHeader);
  tab.appendChild(browserRow);

  // --- Live status updates from event bus ---
  const unsubProviderStatus = eventBus.on('ai:provider:status', ({ providerId, available }) => {
    const dot = statusDots.get(providerId);
    if (dot) {
      dot.style.background = available ? 'var(--accent-green)' : 'var(--severity-info)';
    }
    // Remove any "Discovering..." label for this provider (scoped to this tab)
    const hint = tab.querySelector<HTMLElement>(`.discovery-hint[data-provider="${providerId}"]`);
    if (hint) hint.remove();
  });
  panelCleanupFns.push(unsubProviderStatus);

  // Initial model discovery
  refreshOllamaModels();
  refreshOpenRouterModels();

  // Show "Discovering..." hints for each provider
  for (const [id] of statusDots) {
    const dotEl = statusDots.get(id);
    if (dotEl?.parentElement) {
      const hint = document.createElement('span');
      hint.className = 'discovery-hint';
      hint.dataset.provider = id;
      hint.style.cssText = 'font-size: 10px; color: var(--text-muted); margin-left: 4px;';
      hint.textContent = 'Discovering\u2026';
      dotEl.parentElement.appendChild(hint);
    }
  }

  // Refresh status dots from current availability
  discoverAvailable().then(results => {
    if (isDisposed()) return;
    for (const [id, available] of results) {
      const dot = statusDots.get(id);
      if (dot) dot.style.background = available ? 'var(--accent-green)' : 'var(--severity-info)';
      // Remove discovery hint (scoped to this tab)
      const hint = tab.querySelector<HTMLElement>(`.discovery-hint[data-provider="${id}"]`);
      if (hint) hint.remove();
    }
  }).catch((err) => console.warn('[Sentinel] Provider discovery failed:', err));

  return tab;
}

// ========================================================
// Tab 2: API Keys
// ========================================================

/** Map source IDs to their secret key name(s). */
const SOURCE_SECRET_MAP: Record<string, string> = {
  acled: SECRET_KEYS.ACLED_PASSWORD,
  vesselfinder: SECRET_KEYS.VESSELFINDER_API_KEY,
  gdacs: SECRET_KEYS.GDACS_API_KEY,
};

/** Build a single secret-input row for the API Keys tab. */
function buildSecretInputRow(opts: {
  label: string;
  description: string;
  storeKey?: string;
  envFallback?: string;
  envCheckKey?: string;
  linkUrl?: string;
  linkText?: string;
  isPassword?: boolean;
  signal?: AbortSignal;
  isDisposed?: () => boolean;
}): HTMLElement {
  const row = document.createElement('div');
  row.style.cssText = `
    margin-bottom: 10px; padding: 8px 10px;
    background: var(--bg-secondary); border-radius: 6px;
  `;

  const header = document.createElement('div');
  header.style.cssText = 'display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px;';
  const label = document.createElement('span');
  label.style.cssText = 'font-size: 12px; font-weight: 600; color: var(--text-primary);';
  label.textContent = opts.label;
  header.appendChild(label);
  if (opts.linkUrl) {
    const link = document.createElement('a');
    link.href = opts.linkUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.style.cssText = 'font-size: 10px; color: var(--accent); text-decoration: none;';
    link.textContent = opts.linkText ?? 'Get key \u2192';
    header.appendChild(link);
  }
  row.appendChild(header);

  const desc = document.createElement('div');
  desc.style.cssText = 'font-size: 11px; color: var(--text-muted); margin-bottom: 6px;';
  desc.textContent = opts.description;
  row.appendChild(desc);

  const input = document.createElement('input');
  input.type = opts.isPassword !== false ? 'password' : 'text';
  input.placeholder = opts.envFallback ? `Set via ${opts.envFallback}` : opts.label;
  input.readOnly = !IS_DESKTOP;
  input.style.cssText = `
    width: 100%; box-sizing: border-box; padding: 6px 8px;
    font-size: 12px; background: var(--bg-primary); color: var(--text-primary);
    border: 1px solid var(--border-primary); border-radius: 4px; outline: none;
    ${!IS_DESKTOP ? 'opacity: 0.6; cursor: not-allowed;' : ''}
  `;

  // Load from keychain if storeKey provided
  if (opts.storeKey) {
    secretStore.get(opts.storeKey).then(val => {
      if (opts.isDisposed?.()) return;
      if (val) {
        input.value = val;
        input.placeholder = '';
      }
    }).catch((err: unknown) => console.warn('[Sentinel] Keychain error:', err));
  }

  // Show env dots placeholder when no keychain key but env var is configured
  if (!opts.storeKey && opts.envCheckKey) {
    try {
      const envVal = (import.meta.env as Record<string, string | undefined>)[opts.envCheckKey];
      if (envVal) input.placeholder = '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022 (configured)';
    } catch { /* ignore */ }
  }

  // Desktop: save to keychain on change
  if (IS_DESKTOP && opts.storeKey) {
    const storeKey = opts.storeKey;
    const debouncedSave = debounce(() => {
      if (opts.isDisposed?.()) return;
      secretStore.set(storeKey, input.value).catch((err: unknown) => console.warn('[Sentinel] Keychain error:', err));
    }, 500);
    input.addEventListener('input', debouncedSave, opts.signal ? { signal: opts.signal } : undefined);
  }

  row.appendChild(input);

  // Keychain badge only when we manage the key on desktop
  if (IS_DESKTOP && opts.storeKey) {
    row.appendChild(keychainBadge());
  }

  return row;
}

function buildAPIKeysTab(signal: AbortSignal, isDisposed: () => boolean): HTMLElement {
  const tab = document.createElement('div');
  tab.style.cssText = 'padding: 8px 0;';

  const note = document.createElement('div');
  note.style.cssText = 'font-size: 11px; color: var(--text-muted); margin-bottom: 12px;';
  note.textContent = IS_DESKTOP
    ? '\u{1F512} Keys are stored in OS secure storage.'
    : 'Keys are set via environment variables (read-only in web mode).';
  tab.appendChild(note);

  // Group sources that require API keys
  const keySources = SOURCE_REGISTRY.filter(s => s.requiresApiKey);

  for (const source of keySources) {
    tab.appendChild(buildSecretInputRow({
      label: `${source.name} API Key`,
      description: source.description,
      storeKey: SOURCE_SECRET_MAP[source.id],
      envFallback: source.apiKeyEnv,
      envCheckKey: source.apiKeyEnv,
      linkUrl: source.apiKeyUrl,
      signal,
      isDisposed,
    }));
  }

  // --- ACLED email (required for OAuth token exchange) ---
  tab.appendChild(buildSecretInputRow({
    label: 'ACLED Email',
    description: 'Email address registered with ACLED (used for OAuth authentication)',
    storeKey: SECRET_KEYS.ACLED_EMAIL,
    envFallback: 'VITE_ACLED_EMAIL',
    isPassword: false,
    signal,
    isDisposed,
  }));

  // --- OpenSky Network credentials (optional auth for higher rate limits) ---
  tab.appendChild(buildSecretInputRow({
    label: 'OpenSky Username',
    description: 'Optional — authenticated users get higher rate limits',
    storeKey: SECRET_KEYS.OPENSKY_USERNAME,
    envFallback: 'VITE_OPENSKY_USERNAME',
    linkUrl: 'https://opensky-network.org/index.php?option=com_users&view=registration',
    linkText: 'Register \u2192',
    isPassword: false,
    signal,
    isDisposed,
  }));

  tab.appendChild(buildSecretInputRow({
    label: 'OpenSky Password',
    description: 'Password for OpenSky Network account',
    storeKey: SECRET_KEYS.OPENSKY_PASSWORD,
    envFallback: 'VITE_OPENSKY_PASSWORD',
    isPassword: true,
    signal,
    isDisposed,
  }));

  // MapTiler key (not in SOURCE_REGISTRY but needed)
  tab.appendChild(buildSecretInputRow({
    label: 'MapTiler API Key',
    description: 'Dark basemap tiles for the map view',
    storeKey: SECRET_KEYS.MAPTILER_API_KEY,
    envFallback: IS_DESKTOP ? undefined : 'VITE_MAPTILER_KEY',
    linkUrl: 'https://cloud.maptiler.com/account/keys/',
    signal,
    isDisposed,
  }));

  // --- Upstash Redis (web deployment caching) ---
  tab.appendChild(sectionLabel('Infrastructure'));

  tab.appendChild(buildSecretInputRow({
    label: 'Upstash Redis URL',
    description: 'Redis connection URL for response caching (web deployment)',
    storeKey: SECRET_KEYS.UPSTASH_REDIS_URL,
    envFallback: 'VITE_UPSTASH_REDIS_URL',
    isPassword: false,
    signal,
    isDisposed,
  }));

  tab.appendChild(buildSecretInputRow({
    label: 'Upstash Redis Token',
    description: 'Redis authentication token',
    storeKey: SECRET_KEYS.UPSTASH_REDIS_TOKEN,
    envFallback: 'VITE_UPSTASH_REDIS_TOKEN',
    signal,
    isDisposed,
  }));

  // Cross-reference to AI provider keys
  tab.appendChild(sectionLabel('AI Provider Keys'));
  const aiNote = document.createElement('div');
  aiNote.style.cssText = 'font-size: 11px; color: var(--text-muted);';
  aiNote.textContent = 'Anthropic and OpenRouter keys are configured in the LLMs tab.';
  tab.appendChild(aiNote);

  return tab;
}

// ========================================================
// Tab 3: Debug
// ========================================================

function buildDebugTab(signal: AbortSignal, isDisposed: () => boolean): HTMLElement {
  const tab = document.createElement('div');
  tab.style.cssText = 'padding: 8px 0; display: flex; flex-direction: column; height: 100%;';

  // Verbose mode toggle
  const toggleRow = document.createElement('div');
  toggleRow.style.cssText = 'display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px;';

  const toggleLabel = document.createElement('span');
  toggleLabel.style.cssText = 'font-size: 12px; color: var(--text-primary);';
  toggleLabel.textContent = 'Verbose logging';
  toggleRow.appendChild(toggleLabel);

  const toggleInput = document.createElement('input');
  toggleInput.type = 'checkbox';
  toggleInput.checked = localStorage.getItem(VERBOSE_KEY) === 'true';
  toggleInput.style.cssText = 'cursor: pointer;';
  toggleInput.addEventListener('change', () => {
    localStorage.setItem(VERBOSE_KEY, String(toggleInput.checked));
  }, { signal });
  toggleRow.appendChild(toggleInput);
  tab.appendChild(toggleRow);

  // Clear log button
  const clearBtn = document.createElement('button');
  clearBtn.type = 'button';
  clearBtn.textContent = 'Clear log';
  clearBtn.style.cssText = `
    font-size: 11px; padding: 4px 10px; border-radius: 4px; cursor: pointer;
    background: transparent; color: var(--text-secondary); border: 1px solid var(--border-primary);
    margin-bottom: 8px; align-self: flex-start;
  `;

  // Request log list
  const logList = document.createElement('div');
  logList.style.cssText = `
    flex: 1; overflow-y: auto; font-family: monospace; font-size: 11px;
    background: var(--bg-primary); border: 1px solid var(--border-primary);
    border-radius: 4px; padding: 4px;
  `;

  function renderLogEntry(entry: RequestLogEntry): HTMLElement {
    const row = document.createElement('div');
    row.style.cssText = `
      padding: 3px 4px; border-bottom: 1px solid var(--border-primary);
      display: flex; gap: 6px; align-items: flex-start;
    `;

    const time = document.createElement('span');
    time.style.cssText = 'color: var(--text-muted); white-space: nowrap; flex-shrink: 0;';
    time.textContent = entry.timestamp.slice(11, 19); // HH:MM:SS
    row.appendChild(time);

    const statusColor = entry.status >= 200 && entry.status < 300
      ? 'var(--accent-green)'
      : entry.status >= 400 ? 'var(--accent-red)' : 'var(--accent-amber)';
    const status = document.createElement('span');
    status.style.cssText = `color: ${statusColor}; flex-shrink: 0; width: 28px;`;
    status.textContent = String(entry.status);
    row.appendChild(status);

    const latency = document.createElement('span');
    latency.style.cssText = 'color: var(--text-muted); flex-shrink: 0; width: 50px; text-align: right;';
    latency.textContent = `${entry.latencyMs}ms`;
    row.appendChild(latency);

    const url = document.createElement('span');
    url.style.cssText = 'color: var(--text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1;';
    url.textContent = `${entry.method} ${entry.url}`;
    url.title = `${entry.method} ${entry.url}`;
    row.appendChild(url);

    if (entry.error) {
      const err = document.createElement('div');
      err.style.cssText = 'color: var(--accent-red); font-size: 10px; padding-left: 4px;';
      err.textContent = entry.error;
      const wrapper = document.createElement('div');
      wrapper.appendChild(row);
      wrapper.appendChild(err);
      return wrapper;
    }

    return row;
  }

  function renderFullLog(): void {
    logList.textContent = '';
    const entries = getRequestLog();
    if (entries.length === 0) {
      const empty = document.createElement('div');
      empty.style.cssText = 'padding: 12px; text-align: center; color: var(--text-muted);';
      empty.textContent = 'No requests logged yet';
      logList.appendChild(empty);
      return;
    }
    for (const entry of entries) {
      logList.appendChild(renderLogEntry(entry));
    }
  }

  clearBtn.addEventListener('click', () => {
    clearRequestLog();
    renderFullLog();
  }, { signal });

  // Live updates
  const unsubLogEntry = onLogEntry((entry) => {
    if (isDisposed()) return;

    // Remove "no requests" placeholder if present
    const empty = logList.querySelector('div[style*="text-align: center"]');
    if (empty) empty.remove();

    logList.insertBefore(renderLogEntry(entry), logList.firstChild);

    // Trim DOM nodes to 200
    while (logList.children.length > 200) {
      logList.removeChild(logList.lastChild!);
    }
  });
  panelCleanupFns.push(unsubLogEntry);

  tab.appendChild(clearBtn);
  tab.appendChild(logList);

  // Initial render
  renderFullLog();

  return tab;
}

// ========================================================
// Tab 4: Config (sources + keyboard shortcuts)
// ========================================================

/** Load disabled source IDs from localStorage. */
function loadDisabledSources(): Set<string> {
  try {
    const raw = localStorage.getItem(DISABLED_SOURCES_KEY);
    if (raw) return new Set(JSON.parse(raw) as string[]);
  } catch { /* ignore corrupt data */ }
  return new Set();
}

/** Save disabled source IDs to localStorage. */
function saveDisabledSources(disabled: Set<string>): void {
  localStorage.setItem(DISABLED_SOURCES_KEY, JSON.stringify([...disabled]));
}

function buildConfigTab(signal: AbortSignal): HTMLElement {
  const tab = document.createElement('div');
  tab.style.cssText = 'padding: 8px 0;';

  // --- Source Enable/Disable ---
  tab.appendChild(sectionLabel('Data Sources'));

  const sourceNote = document.createElement('div');
  sourceNote.style.cssText = 'font-size: 11px; color: var(--text-muted); margin-bottom: 8px;';
  sourceNote.textContent = 'Toggle data sources on or off. Disabled sources will not be fetched.';
  tab.appendChild(sourceNote);

  const disabled = loadDisabledSources();

  const sourceList = document.createElement('div');
  sourceList.style.cssText = 'display: flex; flex-direction: column; gap: 2px; margin-bottom: 16px;';

  for (const source of SOURCE_REGISTRY) {
    const row = document.createElement('div');
    row.style.cssText = `
      display: flex; align-items: center; gap: 8px;
      padding: 6px 8px; border-radius: 4px;
      background: var(--bg-secondary);
    `;

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.checked = !disabled.has(source.id);
    checkbox.style.cssText = 'cursor: pointer; flex-shrink: 0;';
    checkbox.setAttribute('aria-label', `Enable ${source.name}`);
    checkbox.addEventListener('change', () => {
      if (checkbox.checked) {
        disabled.delete(source.id);
      } else {
        disabled.add(source.id);
      }
      saveDisabledSources(disabled);
      eventBus.emit('source:status', { sourceId: source.id, state: checkbox.checked ? 'enabled' : 'disabled' });
    }, { signal });
    row.appendChild(checkbox);

    const info = document.createElement('div');
    info.style.cssText = 'flex: 1; min-width: 0;';

    const name = document.createElement('div');
    name.style.cssText = 'font-size: 12px; color: var(--text-primary);';
    name.textContent = source.name;
    info.appendChild(name);

    const desc = document.createElement('div');
    desc.style.cssText = 'font-size: 10px; color: var(--text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;';
    desc.textContent = source.description;
    info.appendChild(desc);

    row.appendChild(info);

    // Interval badge
    if (source.updateIntervalMs) {
      const interval = document.createElement('span');
      interval.style.cssText = 'font-size: 9px; color: var(--text-muted); font-family: var(--font-mono); flex-shrink: 0;';
      const mins = Math.round(source.updateIntervalMs / 60_000);
      interval.textContent = mins >= 60 ? `${Math.round(mins / 60)}h` : `${mins}m`;
      interval.title = `Updates every ${mins} minutes`;
      row.appendChild(interval);
    }

    sourceList.appendChild(row);
  }

  tab.appendChild(sourceList);

  // --- Keyboard Shortcuts ---
  tab.appendChild(sectionLabel('Keyboard Shortcuts'));

  const shortcutNote = document.createElement('div');
  shortcutNote.style.cssText = 'font-size: 11px; color: var(--text-muted); margin-bottom: 8px;';
  shortcutNote.textContent = 'Active keyboard shortcuts for quick navigation.';
  tab.appendChild(shortcutNote);

  const bindings = getActiveBindings();
  const shortcutList = document.createElement('div');
  shortcutList.style.cssText = 'display: flex; flex-direction: column; gap: 2px;';

  for (const binding of bindings) {
    const row = document.createElement('div');
    row.style.cssText = `
      display: flex; align-items: center; justify-content: space-between;
      padding: 5px 8px; border-radius: 4px;
      background: var(--bg-secondary);
    `;

    const label = document.createElement('span');
    label.style.cssText = 'font-size: 12px; color: var(--text-primary);';
    label.textContent = binding.label;
    row.appendChild(label);

    const kbd = document.createElement('kbd');
    kbd.style.cssText = `
      font-size: 11px; font-family: var(--font-mono);
      padding: 2px 6px; border-radius: 3px;
      background: var(--bg-primary); color: var(--text-secondary);
      border: 1px solid var(--border-primary);
    `;
    kbd.textContent = formatShortcut(binding);
    row.appendChild(kbd);

    shortcutList.appendChild(row);
  }

  tab.appendChild(shortcutList);

  // --- Notifications ---
  tab.appendChild(sectionLabel('Notifications'));

  const notifNote = document.createElement('div');
  notifNote.style.cssText = 'font-size: 11px; color: var(--text-muted); margin-bottom: 8px;';
  notifNote.textContent = isNotificationSupported()
    ? 'Receive OS notifications for breaking news, surge alerts, and hotspot escalations.'
    : 'Notifications are not supported in this browser.';
  tab.appendChild(notifNote);

  if (isNotificationSupported()) {
    const notifSettings = loadNotificationSettings();

    // Enable/disable toggle
    const enableRow = document.createElement('div');
    enableRow.style.cssText = `
      display: flex; align-items: center; gap: 8px;
      padding: 6px 8px; border-radius: 4px;
      background: var(--bg-secondary); margin-bottom: 4px;
    `;

    const enableCheckbox = document.createElement('input');
    enableCheckbox.type = 'checkbox';
    enableCheckbox.checked = notifSettings.enabled;
    enableCheckbox.style.cssText = 'cursor: pointer; flex-shrink: 0;';
    enableCheckbox.setAttribute('aria-label', 'Enable notifications');
    enableCheckbox.addEventListener('change', async () => {
      if (enableCheckbox.checked) {
        const granted = await requestNotificationPermission();
        if (!granted) {
          enableCheckbox.checked = false;
          return;
        }
      }
      notifSettings.enabled = enableCheckbox.checked;
      saveNotificationSettings(notifSettings);
    }, { signal });
    enableRow.appendChild(enableCheckbox);

    const enableLabel = document.createElement('span');
    enableLabel.style.cssText = 'font-size: 12px; color: var(--text-primary);';
    enableLabel.textContent = 'Enable desktop notifications';
    enableRow.appendChild(enableLabel);

    tab.appendChild(enableRow);

    // Severity threshold
    const thresholdRow = document.createElement('div');
    thresholdRow.style.cssText = `
      display: flex; align-items: center; justify-content: space-between;
      padding: 6px 8px; border-radius: 4px;
      background: var(--bg-secondary);
    `;

    const thresholdLabel = document.createElement('span');
    thresholdLabel.style.cssText = 'font-size: 12px; color: var(--text-primary);';
    thresholdLabel.textContent = 'Minimum severity';
    thresholdRow.appendChild(thresholdLabel);

    const thresholdSelect = document.createElement('select');
    thresholdSelect.style.cssText = `
      font-size: 11px; padding: 2px 4px; border-radius: 3px;
      background: var(--bg-primary); color: var(--text-primary);
      border: 1px solid var(--border-primary); cursor: pointer;
    `;
    const severityOptions: Array<{ value: NotificationSettings['minSeverity']; label: string }> = [
      { value: 'critical', label: 'Critical only' },
      { value: 'high', label: 'High & Critical' },
      { value: 'medium', label: 'Medium & above' },
    ];
    for (const opt of severityOptions) {
      const option = document.createElement('option');
      option.value = opt.value;
      option.textContent = opt.label;
      if (opt.value === notifSettings.minSeverity) option.selected = true;
      thresholdSelect.appendChild(option);
    }
    thresholdSelect.addEventListener('change', () => {
      notifSettings.minSeverity = thresholdSelect.value as NotificationSettings['minSeverity'];
      saveNotificationSettings(notifSettings);
    }, { signal });
    thresholdRow.appendChild(thresholdSelect);

    tab.appendChild(thresholdRow);
  }

  return tab;
}

// ========================================================
// Settings panel (main)
// ========================================================

export function createSettingsPanel(): HTMLElement {
  // Clean up previous listeners on rebuild
  for (const fn of panelCleanupFns) fn();
  panelCleanupFns = [];

  // AbortController: removes all DOM event listeners atomically on cleanup
  const ac = new AbortController();
  let disposed = false;
  const isDisposed = () => disposed;

  panelCleanupFns.push(() => {
    ac.abort();
    disposed = true;
  });

  const container = document.createElement('div');
  container.className = 'settings-panel';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%;';

  // Tab bar
  const tabBar = document.createElement('div');
  tabBar.setAttribute('role', 'tablist');
  tabBar.style.cssText = `
    display: flex; border-bottom: 1px solid var(--border-primary);
    padding: 0 8px; flex-shrink: 0;
  `;

  const tabs = ['LLMs', 'API Keys', 'Debug', 'Config'] as const;
  const tabBodies: HTMLElement[] = [];
  const tabButtons: HTMLButtonElement[] = [];

  const prefs = loadPreferences();
  const onPreferencesChange = () => {
    savePreferences(prefs);
    refreshProviders();
  };

  const body = document.createElement('div');
  body.id = 'settings-tabpanel';
  body.setAttribute('role', 'tabpanel');
  body.style.cssText = 'flex: 1; overflow-y: auto; padding: 0 8px;';

  // Build tab content
  tabBodies.push(buildLLMsTab(prefs, onPreferencesChange, ac.signal, isDisposed));
  tabBodies.push(buildAPIKeysTab(ac.signal, isDisposed));
  tabBodies.push(buildDebugTab(ac.signal, isDisposed));
  tabBodies.push(buildConfigTab(ac.signal));

  for (let i = 0; i < tabs.length; i++) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.id = `settings-tab-${i}`;
    btn.textContent = tabs[i];
    btn.setAttribute('role', 'tab');
    btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
    btn.setAttribute('aria-controls', 'settings-tabpanel');
    btn.setAttribute('tabindex', i === 0 ? '0' : '-1');
    btn.style.cssText = `
      flex: 1; padding: 8px 4px; font-size: 11px; font-weight: 600;
      background: none; border: none; cursor: pointer;
      color: var(--text-muted); border-bottom: 2px solid transparent;
    `;
    btn.addEventListener('click', () => switchTab(i), { signal: ac.signal });
    tabBar.appendChild(btn);
    tabButtons.push(btn);
  }

  // Arrow key navigation for settings tabs
  tabBar.addEventListener('keydown', (e: KeyboardEvent) => {
    const target = e.target as HTMLElement;
    const idx = tabButtons.indexOf(target as HTMLButtonElement);
    if (idx === -1) return;

    let nextIdx = -1;
    if (e.key === 'ArrowRight') nextIdx = (idx + 1) % tabButtons.length;
    else if (e.key === 'ArrowLeft') nextIdx = (idx - 1 + tabButtons.length) % tabButtons.length;
    else if (e.key === 'Home') nextIdx = 0;
    else if (e.key === 'End') nextIdx = tabButtons.length - 1;

    if (nextIdx >= 0 && nextIdx !== idx) {
      e.preventDefault();
      tabButtons[nextIdx].focus();
      switchTab(nextIdx);
    }
  }, { signal: ac.signal });

  function switchTab(idx: number): void {
    for (let i = 0; i < tabs.length; i++) {
      tabButtons[i].style.color = i === idx ? 'var(--text-primary)' : 'var(--text-muted)';
      tabButtons[i].style.borderBottomColor = i === idx ? 'var(--accent)' : 'transparent';
      tabButtons[i].setAttribute('aria-selected', i === idx ? 'true' : 'false');
      tabButtons[i].setAttribute('tabindex', i === idx ? '0' : '-1');
    }
    body.setAttribute('aria-labelledby', `settings-tab-${idx}`);
    body.textContent = '';
    body.appendChild(tabBodies[idx]);
  }

  container.appendChild(tabBar);
  container.appendChild(body);

  // Default to LLMs tab
  switchTab(0);

  // Expose destroy() on the element so teardownApp/panelManager can clean up
  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const fn of panelCleanupFns) fn();
    panelCleanupFns = [];
  };

  return container;
}
